import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Alert } from '@clay-run/glaze/alert';
import { Button } from '@clay-run/glaze/button';
import { Heading } from '@clay-run/glaze/heading';
import { Text } from '@clay-run/glaze/text';
import { TextareaField } from '@clay-run/glaze/textarea-field';
import { useAppBasePath } from '../../_shared/base-path';
import {
  useAdRequest,
  useCreativeImages,
  useMetaStatus,
  useMetaVideo,
  useMetaVideoStates,
  usePublishAdRequest,
  useReviewAdRequest,
} from '../api-client';
import { creativeCopy, hasOverrides } from '../draft';
import { questionLabel } from '../lead-forms';
import { adName } from '../naming';
import { usd } from '../format';
import { META_CTA_LABELS, PLATFORM_LABELS, type AdRequestDetail, type RequestCreative } from '../types';
import { PlacementToggle, type MediaSlot } from './AdDraftPanel';
import { MetaFeedPreview, MetaStoryPreview } from './AdPreviews';
import { Panel } from './Panels';
import { LinkedInPublishedAlert, LinkedInRequestBody } from './LinkedInRequestDetail';
import { RequestEditor } from './RequestEditor';
import type { RequestPageState } from '../publish-notice';
import { RequestStatusBadge } from './StatusBadge';

type Decision = 'changes_requested' | 'rejected';


function ReviewNoteForm({ decision, requestId, onDone }: { decision: Decision; requestId: string; onDone: () => void }) {
  const [note, setNote] = useState('');
  const review = useReviewAdRequest(requestId);
  return (
    <Panel className="flex flex-col gap-3">
      <TextareaField
        label={decision === 'rejected' ? 'Why is this rejected?' : 'What should change?'}
        description="The requester sees this note on the request."
        rows={3}
        value={note}
        onValueChange={setNote}
      />
      <div className="flex gap-2">
        <Button
          variant={decision === 'rejected' ? 'danger' : 'primary'}
          disabled={!note.trim() || review.isPending}
          loading={review.isPending}
          onClick={() => review.mutate({ decision, note: note.trim() }, { onSuccess: onDone })}
        >
          {decision === 'rejected' ? 'Reject request' : 'Send back for changes'}
        </Button>
        <Button variant="secondary" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </Panel>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-content-tertiary">{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

function publishBlockerFor(creatives: RequestCreative[], videoStates: Map<string, string>): string | null {
  if (creatives.length === 0) return 'This request has no creatives';
  for (const c of creatives) {
    const which = creatives.length > 1 ? ` for ${c.name}` : '';
    if (!c.hasImage) return `There's no image${which}`;
    if (c.verticalVideoId && !c.hasVerticalImage) return `The 9:16 video${which} has no thumbnail`;
    for (const videoId of [c.videoId, c.verticalVideoId]) {
      const state = videoId ? videoStates.get(videoId) : null;
      if (state === 'processing') return `Meta is still processing a video${which}`;
      if (state === 'error') return `Meta couldn't process a video${which}`;
    }
  }
  return null;
}

const placementLabel = (c: RequestCreative) => (c.hasVerticalImage ? ' · 4:5 + 9:16' : '');

function CreativeSwitcher({
  creatives,
  imageUrls,
  selectedId,
  onSelect,
}: {
  creatives: RequestCreative[];
  imageUrls: Map<string, string>;
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="mb-3 flex flex-wrap gap-1.5">
      {creatives.map(c => (
        <button
          key={c.id}
          type="button"
          onClick={() => onSelect(c.id)}
          className={`flex items-center gap-1.5 rounded-lg border py-1 pl-1 pr-2 text-xs ${
            c.id === selectedId ? 'border-border-action bg-bg-secondary' : 'border-border-primary'
          }`}
        >
          {imageUrls.get(c.id) ? (
            <img src={imageUrls.get(c.id)} alt="" className="size-6 rounded object-cover" />
          ) : (
            <span className="size-6 rounded bg-bg-secondary" />
          )}
          <span className="max-w-28 truncate">{c.name}</span>
        </button>
      ))}
    </div>
  );
}

function RequestDetail({ request }: { request: AdRequestDetail }) {
  const base = useAppBasePath();
  const notice = (useLocation().state as RequestPageState | null)?.notice;
  const [decision, setDecision] = useState<Decision | null>(null);
  const [editing, setEditing] = useState(false);
  const images = useCreativeImages(request.id, request.creatives);
  const videoStates = useMetaVideoStates(request.creatives.flatMap(c => [c.videoId, c.verticalVideoId].filter(v => v !== null)));
  const [selectedId, setSelectedId] = useState(request.creatives[0]?.id ?? '');
  const [placement, setPlacement] = useState<MediaSlot>('feed');
  const selected = request.creatives.find(c => c.id === selectedId) ?? request.creatives[0] ?? null;
  const shownVideoId = (placement === 'vertical' ? selected?.verticalVideoId : selected?.videoId) ?? null;
  const video = useMetaVideo(shownVideoId);
  const videoState = shownVideoId ? (video.data?.state ?? 'processing') : null;
  const previewContent = {
    ...request,
    ...(selected && creativeCopy(request, selected.overrides)),
    imageUrl: selected ? (images.urls[placement].get(selected.id) ?? null) : null,
    videoUrl: shownVideoId ? (video.data?.sourceUrl ?? null) : null,
  };
  const publishBlocker = request.linkedin ? null : publishBlockerFor(request.creatives, videoStates);
  const publishedAds = request.creatives.filter(c => c.metaAdId);
  const status = useMetaStatus();
  const publish = usePublishAdRequest(request.id);

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center gap-3">
        <div className="flex flex-col gap-1">
          <Link to={base} className="text-xs text-content-tertiary hover:underline">
            Review queue
          </Link>
          <div className="flex items-center gap-3">
            <Heading level={1} size="lg">
              {request.name}
            </Heading>
            <RequestStatusBadge status={request.status} />
          </div>
        </div>
        {!editing && (request.canReview || request.canEdit) && (
          <div className="ml-auto flex gap-2">
            {request.canEdit && (
              <Button
                variant="secondary"
                onClick={() => setEditing(true)}
                disabled={publish.isPending || images.loading}
              >
                {request.canResubmit ? 'Edit and resubmit' : 'Edit'}
              </Button>
            )}
          </div>
        )}
        {!editing && request.canReview && (
          <div className="flex gap-2">
            <Button variant="danger-secondary" onClick={() => setDecision('rejected')} disabled={publish.isPending}>
              Reject
            </Button>
            <Button variant="secondary" onClick={() => setDecision('changes_requested')} disabled={publish.isPending}>
              Request changes
            </Button>
            <Button
              variant="primary"
              onClick={() => publish.mutate()}
              disabled={publishBlocker !== null || publish.isPending}
              loading={publish.isPending}
              title={publishBlocker ?? undefined}
            >
              {request.linkedin ? 'Publish to LinkedIn as a draft' : 'Publish paused to Meta'}
            </Button>
          </div>
        )}
      </header>

      {decision && <ReviewNoteForm decision={decision} requestId={request.id} onDone={() => setDecision(null)} />}
      {publish.error && (
        <Alert status="error" error={publish.error}>
          {publish.error.message}
        </Alert>
      )}
      {notice && request.status === 'needs_review' && !publish.error && <Alert status="warning">{notice}</Alert>}
      {!publish.error && !notice && request.publishError && request.status === 'needs_review' && (
        <Alert status="warning">Last publish attempt failed: {request.publishError}</Alert>
      )}
      {request.linkedin && <LinkedInPublishedAlert fields={request.linkedin} />}
      {request.meta && (
        <Alert status={publishedAds.length > 0 ? 'success' : 'warning'}>
          {publishedAds.length > 0
            ? `Created in Meta, all paused${publishedAds.length > 1 ? `: ${publishedAds.length} ads in one ad set` : ''}. Activate it in Ads Manager when it should start spending. `
            : "Campaign and ad set created in Meta, paused. The ads themselves weren't created: they need a Facebook Page, which the sandbox account can't use. "}
          <Link to={`${base}/performance/meta/${request.meta.campaignId}`} className="font-medium underline">
            View campaign
          </Link>
        </Alert>
      )}
      {(request.status === 'changes_requested' || request.status === 'rejected') && request.reviewNote && (
        <Alert status={request.status === 'rejected' ? 'error' : 'info'} disableErrorLogging>
          {request.reviewerEmail ?? 'A reviewer'}: {request.reviewNote}
        </Alert>
      )}

      {request.linkedin ? (
        <LinkedInRequestBody request={request} fields={request.linkedin} />
      ) : editing ? (
        <RequestEditor
          request={request}
          imageUrls={images.urls}
          onDone={() => setEditing(false)}
        />
      ) : (
        <div className="flex flex-wrap gap-8">
          <div className="w-80 shrink-0">
            {request.creatives.length > 1 && selected && (
              <CreativeSwitcher
                creatives={request.creatives}
                imageUrls={images.urls.feed}
                selectedId={selected.id}
                onSelect={setSelectedId}
              />
            )}
            <div className="mb-3">
              <PlacementToggle value={placement} onChange={setPlacement} />
            </div>
            {placement === 'vertical' ? <MetaStoryPreview content={previewContent} /> : <MetaFeedPreview content={previewContent} />}
            {videoState === 'processing' && (
              <Text size="xs" variant="tertiary" className="mt-2">
                Meta is processing the video{video.data?.progress != null ? ` (${video.data.progress}%)` : ''}. The preview
                shows its thumbnail until it's ready.
              </Text>
            )}
            {videoState === 'error' && (
              <Text size="xs" className="mt-2 text-text-red">
                Meta couldn't process this video. Ask the requester to upload it again.
              </Text>
            )}
          </div>
          <dl className="grid h-fit min-w-0 max-w-xl flex-1 grid-cols-[160px_1fr] gap-x-4 gap-y-2 text-sm">
            <Detail label="Team">{request.team || '—'}</Detail>
            <Detail label="Requested by">{request.requesterEmail ?? '—'}</Detail>
            <Detail label="Submitted">{new Date(request.submittedAt).toLocaleString()}</Detail>
            <Detail label="Platforms">{request.platforms.map(p => PLATFORM_LABELS[p]).join(', ')}</Detail>
            {request.program && <Detail label="Program">{request.program}</Detail>}
            {request.personas.length > 0 && (
              <Detail label={request.personas.length > 1 ? 'Personas' : 'Persona'}>{request.personas.join(', ')}</Detail>
            )}
            {request.adSetName && <Detail label="Ad set name">{request.adSetName}</Detail>}
            <Detail label="Targets">
              {request.targeting.included.length > 0 ? request.targeting.included.map(a => a.name).join(', ') : 'No custom audience'}
            </Detail>
            <Detail label="Excluded">
              {request.targeting.excluded.length > 0 ? request.targeting.excluded.map(a => a.name).join(', ') : 'None'}
            </Detail>
            {request.targeting.droppedExclusions.length > 0 && (
              <Detail label="Dropped exclusions">
                <ul className="flex flex-col gap-0.5 text-content-warning">
                  {request.targeting.droppedExclusions.map(d => (
                    <li key={d.name}>
                      {d.name}: “{d.reason}”
                    </li>
                  ))}
                </ul>
              </Detail>
            )}
            <Detail label="Daily budget">{usd(request.dailyBudgetUsd)}</Detail>
            <Detail label="Dates">
              {request.startDate} → {request.endDate ?? 'no end date'}
            </Detail>
            <Detail label={request.creatives.length > 1 ? 'Creatives' : 'Creative'}>
              <ul className="flex flex-col gap-0.5">
                {request.creatives.map(c => (
                  <li key={c.id}>
                    {adName(request.program, request.personas, c.name)} · {c.creativeType === 'video' ? 'Video' : 'Image'}
                    {placementLabel(c)}
                    {c.id === selected?.id && c.videoId && video.data?.lengthSeconds
                      ? ` · ${Math.round(video.data.lengthSeconds)}s`
                      : ''}
                    {request.creatives.length > 1 && hasOverrides(c) && (
                      <span className="text-content-tertiary"> · own copy</span>
                    )}
                    {c.metaAdId && (
                      <span className="text-content-tertiary"> · ad {c.metaAdId}</span>
                    )}
                  </li>
                ))}
              </ul>
            </Detail>
            <Detail label="Call to action">{META_CTA_LABELS[request.callToAction]}</Detail>
            <Detail label="Destination">
              {request.leadFormSpec ? (
                <div className="flex flex-col gap-0.5">
                  <span>Instant Form: {request.leadFormSpec.name}</span>
                  {request.leadFormSpec.questionHeadline && (
                    <span className="text-content-tertiary">“{request.leadFormSpec.questionHeadline}”</span>
                  )}
                  <span className="text-content-tertiary">
                    {request.leadFormSpec.questions.map(questionLabel).join(', ')} · ends with “{request.leadFormSpec.thankYou.title}”
                  </span>
                  <span className="text-content-tertiary">
                    {request.status === 'published' && request.leadFormId
                      ? `Created in Meta as form ${request.leadFormId}`
                      : 'Created in Meta when published'}
                  </span>
                </div>
              ) : request.leadFormId ? (
                `Instant Form: ${request.leadFormName ?? request.leadFormId}`
              ) : (
                <a href={request.destinationUrl} target="_blank" rel="noopener noreferrer" className="break-all underline">
                  {request.destinationUrl}
                </a>
              )}
            </Detail>
            {request.reviewedAt && (
              <Detail label="Reviewed">
                {new Date(request.reviewedAt).toLocaleString()} by {request.reviewerEmail ?? 'a reviewer'}
              </Detail>
            )}
            {request.canReview && status.data?.isSandbox && (
              <Detail label="Publishes to">Meta sandbox account (ads never deliver)</Detail>
            )}
          </dl>
        </div>
      )}
    </section>
  );
}

export default function RequestDetailPage() {
  const { requestId = '' } = useParams<{ requestId: string }>();
  const { data: request, isLoading, isError } = useAdRequest(requestId);

  if (isLoading) {
    return <Text variant="tertiary">Loading request...</Text>;
  }
  if (isError || !request) {
    return <Text variant="tertiary">This request couldn't be loaded.</Text>;
  }
  return <RequestDetail key={request.id} request={request} />;
}
